import React, { useMemo, useState } from 'react';
import { Loan } from '../types/loan';
import { TrophyIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
interface LeaderboardProps {
  loans: Loan[];
  currentMarketRate: number;
}
export function Leaderboard({
  loans,
  currentMarketRate
}: LeaderboardProps) {
  const rankings = useMemo(() => {
    const stats: Record<string, {
      count: number;
      volume: number;
      rateDiffs: number[];
    }> = {};
    loans.forEach(loan => {
      if (!stats[loan.loanOfficer]) {
        stats[loan.loanOfficer] = {
          count: 0,
          volume: 0,
          rateDiffs: []
        };
      }
      stats[loan.loanOfficer].count += 1;
      stats[loan.loanOfficer].volume += loan.loanAmount;
      stats[loan.loanOfficer].rateDiffs.push(loan.interestRate - currentMarketRate);
    });
    return Object.entries(stats).map(([name, data]) => ({
      name,
      count: data.count,
      volume: data.volume,
      avgRateDiff: data.count > 0 ? data.rateDiffs.reduce((a, b) => a + b, 0) / data.count : 0
    })).sort((a, b) => b.count - a.count);
  }, [loans, currentMarketRate]);
  const formatCurrency = (value: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
  const medalColors = ['bg-amber-100 text-amber-700 ring-amber-300', 'bg-gray-100 text-gray-600 ring-gray-300', 'bg-orange-100 text-orange-700 ring-orange-300'];
  const [expanded, setExpanded] = useState(false);
  const INITIAL_COUNT = 3;
  if (rankings.length === 0) return null;
  const maxCount = rankings[0]?.count ?? 1;
  const visibleRankings = expanded ? rankings : rankings.slice(0, INITIAL_COUNT);
  const hasMore = rankings.length > INITIAL_COUNT;
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-130">
      <div className="px-6 py-5 border-b border-gray-200 flex items-center gap-2" data-id="element-131">
        <TrophyIcon className="w-5 h-5 text-amber-500" data-id="element-132" />
        <h3 className="text-lg font-semibold text-gray-800" data-id="element-133">
          Loan Officer Leaderboard
        </h3>
        <span className="text-sm text-gray-400 ml-1" data-id="element-134">
          (by matching opportunities)
        </span>
      </div>
      <div className="divide-y divide-gray-100" data-id="element-135">
        {visibleRankings.map((officer, index) => {
        const barWidth = maxCount > 0 ? officer.count / maxCount * 100 : 0;
        return <div key={officer.name} className="px-6 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors" data-id="element-136">
              <div className="flex-shrink-0 w-8 text-center" data-id="element-137">
                {index < 3 ? <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ring-1 ring-inset ${medalColors[index]}`} data-id="element-138">
                    {index + 1}
                  </span> : <span className="text-sm font-medium text-gray-400" data-id="element-139">
                    {index + 1}
                  </span>}
              </div>

              <div className="flex-1 min-w-0" data-id="element-140">
                <div className="flex items-center justify-between mb-1.5" data-id="element-141">
                  <span className="text-sm font-medium text-gray-900 truncate" data-id="element-142">
                    {officer.name}
                  </span>
                  <div className="flex items-center gap-4 text-xs text-gray-500 flex-shrink-0 ml-4" data-id="element-143">
                    <span className="tabular-nums" data-id="element-144">
                      {formatCurrency(officer.volume)} volume
                    </span>
                    <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-semibold ring-1 ring-inset text-cyan-700 bg-cyan-50 ring-cyan-600/20 tabular-nums" data-id="element-145">
                      +{officer.avgRateDiff.toFixed(2)}% avg
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-146">
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-147">
                    <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 transition-all duration-500" style={{
                  width: `${barWidth}%`
                }} data-id="element-148" />
                  </div>
                  <span className="text-sm font-semibold text-gray-700 tabular-nums w-16 text-right" data-id="element-149">
                    {officer.count} {officer.count === 1 ? 'loan' : 'loans'}
                  </span>
                </div>
              </div>
            </div>;
      })}
      </div>
      {hasMore && <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/50" data-id="element-150">
          <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-center gap-1.5 text-sm font-medium text-violet-600 hover:text-violet-800 transition-colors py-1" data-id="element-151">
            {expanded ? <>
                <ChevronUpIcon className="w-4 h-4" data-id="element-152" />
                View Less
              </> : <>
                <ChevronDownIcon className="w-4 h-4" data-id="element-153" />
                View More ({rankings.length - INITIAL_COUNT} more)
              </>}
          </button>
        </div>}
    </div>;
}